'use client';

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import {
  CLIENTS, EQUIPMENT,
  type Intervention, type ConnectionType, type InterventionStatus,
} from '@/lib/data';

const CONNECTION_TYPES: ConnectionType[] = ['Na lokaciji', 'VPN', 'VNC', 'RDP', 'AnyDesk', 'TeamViewer'];
const STATUSES: InterventionStatus[] = ['Završena', 'U toku', 'Zakazana'];

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Intervention) => void;
  initial?: Intervention | null;
}

type FormValues = {
  clientId: string;
  date: string;
  time: string;
  connectionType: ConnectionType;
  description: string;
  duration: number;
  status: InterventionStatus;
  equipmentId: string;
  notes: string;
};

function todayStr() {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

function nowTimeStr() {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${pad(n.getHours())}:${pad(n.getMinutes())}`;
}

export default function InterventionForm({ open, onClose, onSave, initial }: Props) {
  const {
    register, handleSubmit, watch, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      clientId: '',
      date: todayStr(),
      time: nowTimeStr(),
      connectionType: 'AnyDesk',
      description: '',
      duration: 60,
      status: 'Završena',
      equipmentId: '',
      notes: '',
    },
  });

  const watchClientId = watch('clientId');
  const clientEquipment = EQUIPMENT.filter(e => e.clientId === watchClientId);
  const selectedClient = CLIENTS.find(c => c.id === watchClientId);

  useEffect(() => {
    if (initial) {
      reset({
        clientId: initial.clientId,
        date: initial.date,
        time: initial.time,
        connectionType: initial.connectionType,
        description: initial.description,
        duration: initial.duration,
        status: initial.status,
        equipmentId: initial.equipmentId || '',
        notes: initial.notes,
      });
    } else {
      reset({
        clientId: '',
        date: todayStr(),
        time: nowTimeStr(),
        connectionType: 'AnyDesk',
        description: '',
        duration: 60,
        status: 'Završena',
        equipmentId: '',
        notes: '',
      });
    }
  }, [initial, reset, open]);

  function onSubmit(data: FormValues) {
    const client = CLIENTS.find(c => c.id === data.clientId);
    const eq = EQUIPMENT.find(e => e.id === data.equipmentId);
    onSave({
      id: initial?.id || `int-${Date.now()}`,
      clientId: data.clientId,
      clientName: client?.name || '',
      date: data.date,
      time: data.time,
      connectionType: data.connectionType,
      description: data.description,
      duration: Number(data.duration),
      status: data.status,
      equipmentId: data.equipmentId || undefined,
      equipmentLabel: eq ? `${eq.brand} ${eq.model}` : undefined,
      notes: data.notes,
    });
  }

  const InputError = ({ msg }: { msg?: string }) =>
    msg ? <p className="error-text">{msg}</p> : null;

  const CT_COLORS: Record<ConnectionType, string> = {
    'Na lokaciji': 'bg-emerald-100 text-emerald-800',
    'VPN': 'bg-violet-100 text-violet-800',
    'VNC': 'bg-orange-100 text-orange-800',
    'RDP': 'bg-blue-100 text-blue-800',
    'AnyDesk': 'bg-rose-100 text-rose-800',
    'TeamViewer': 'bg-cyan-100 text-cyan-800',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? 'Uredi intervenciju' : 'Nova intervencija'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Client & timing */}
        <section>
          <h3
            className="text-sm font-semibold uppercase tracking-wide mb-4"
            style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.07em' }}
          >
            Klijent i vreme
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">
                Klijent <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <select
                className="input-field"
                {...register('clientId', { required: 'Klijent je obavezan' })}
              >
                <option value="">— Izaberite klijenta —</option>
                {CLIENTS.map(c => (
                  <option key={`form-client-${c.id}`} value={c.id}>{c.name}</option>
                ))}
              </select>
              <InputError msg={errors.clientId?.message} />
              {selectedClient && (
                <p className="helper-text">
                  Odgovorno lice: {selectedClient.responsiblePerson} · {selectedClient.city}
                </p>
              )}
            </div>
            <div>
              <label className="label">
                Datum <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <input
                type="date"
                className="input-field"
                {...register('date', { required: 'Datum je obavezan' })}
              />
              <InputError msg={errors.date?.message} />
            </div>
            <div>
              <label className="label">
                Vreme <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <input
                type="time"
                className="input-field"
                {...register('time', { required: 'Vreme je obavezno' })}
              />
              <InputError msg={errors.time?.message} />
            </div>
          </div>
        </section>

        <hr style={{ borderColor: 'hsl(var(--border))' }} />

        {/* Intervention details */}
        <section>
          <h3
            className="text-sm font-semibold uppercase tracking-wide mb-4"
            style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.07em' }}
          >
            Detalji intervencije
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">
                Tip veze / metod <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <select
                className="input-field"
                {...register('connectionType', { required: 'Tip veze je obavezan' })}
              >
                {CONNECTION_TYPES.map(t => (
                  <option key={`form-ct-${t}`} value={t}>{t}</option>
                ))}
              </select>
              <p className="helper-text">Način na koji je izvršena intervencija</p>
              <InputError msg={errors.connectionType?.message} />
            </div>
            <div>
              <label className="label">
                Trajanje (minuti) <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <input
                type="number"
                min={1}
                max={600}
                className="input-field mono"
                placeholder="60"
                {...register('duration', {
                  required: 'Trajanje je obavezno',
                  min: { value: 1, message: 'Minimum 1 minut' },
                  max: { value: 600, message: 'Maksimum 600 minuta' },
                })}
              />
              <p className="helper-text">Ukupno vreme provedeno na intervenciji</p>
              <InputError msg={errors.duration?.message} />
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input-field" {...register('status')}>
                {STATUSES.map(s => (
                  <option key={`form-status-${s}`} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Oprema (opcionalno)</label>
              <select
                className="input-field"
                {...register('equipmentId')}
                disabled={!watchClientId}
              >
                <option value="">— Bez veze sa opremom —</option>
                {clientEquipment.map(eq => (
                  <option key={`form-eq-${eq.id}`} value={eq.id}>
                    {eq.brand} {eq.model} ({eq.type})
                  </option>
                ))}
              </select>
              {!watchClientId && (
                <p className="helper-text">Prvo izaberite klijenta</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label">
                Opis intervencije <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <textarea
                className="input-field resize-none"
                rows={5}
                placeholder="Detaljno opišite šta je urađeno: koji problem je rešen, koje akcije su preduzete, šta je instalirano/konfigurisano..."
                {...register('description', {
                  required: 'Opis je obavezan',
                  minLength: { value: 10, message: 'Opis mora imati najmanje 10 karaktera' },
                })}
              />
              <InputError msg={errors.description?.message} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Interne napomene (opcionalno)</label>
              <textarea
                className="input-field resize-none"
                rows={2}
                placeholder="Napomene koje nisu deo zvaničnog izveštaja..."
                {...register('notes')}
              />
              <p className="helper-text">Ove napomene neće biti prikazane u PDF izveštaju</p>
            </div>
          </div>
        </section>

        {/* Type badges preview */}
        <div
          className="p-3 rounded-xl"
          style={{ backgroundColor: 'hsl(var(--muted) / 0.5)' }}
        >
          <p className="text-xs font-medium mb-2" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Legenda tipova veze:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {CONNECTION_TYPES.map(t => (
              <span key={`legend-${t}`} className={`badge text-xs ${CT_COLORS[t]}`}>
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div
          className="flex justify-end gap-3 pt-2 border-t sticky bottom-0 py-4 -mx-6 px-6"
          style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--card))' }}
        >
          <button type="button" className="btn-secondary" onClick={onClose}>
            Otkaži
          </button>
          <button type="submit" className="btn-primary min-w-[160px]" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Čuvanje...
              </span>
            ) : (initial ? 'Sačuvaj izmene' : 'Evidentiraj intervenciju')}
          </button>
        </div>
      </form>
    </Modal>
  );
}